import React from 'react';
import styles from '../../../../styles/DashboardV2.module.css';

interface CircularProgressWidgetProps {
    value: number; // 0–100
    size?: number;
    strokeWidth?: number;
    completed?: number;
    total?: number;
    trendPct?: number | null;
    label?: string;
}

const getColor = (rate: number) => {
    if (rate >= 90) return '#10b981';
    if (rate >= 70) return '#f59e0b';
    return '#ef4444';
};

const CircularProgressWidget: React.FC<CircularProgressWidgetProps> = ({
    value,
    size = 120,
    strokeWidth = 10,
    completed,
    total,
    trendPct,
    label,
}) => {
    const pct = Math.min(100, Math.max(0, value));
    const radius = (size - strokeWidth * 2) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (pct / 100) * circumference;
    const color = getColor(pct);
    const cx = size / 2;
    const cy = size / 2;

    return (
        <div className={styles.circularWrapper}>
            <svg
                className={styles.circularSvg}
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
            >
                <circle
                    className={styles.circularBg}
                    cx={cx} cy={cy}
                    r={radius}
                    strokeWidth={strokeWidth}
                />
                <circle
                    className={styles.circularFg}
                    cx={cx} cy={cy}
                    r={radius}
                    strokeWidth={strokeWidth}
                    stroke={color}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    transform={`rotate(-90 ${cx} ${cy})`}
                />
                <text x={cx} y={cy - 8} className={styles.circularPct} style={{ fontSize: size < 100 ? 16 : 22, fontWeight: 700, fill: '#0f172a', textAnchor: 'middle', dominantBaseline: 'auto' }}>
                    {pct.toFixed(0)}%
                </text>
                {label && (
                    <text x={cx} y={cy + 10} style={{ fontSize: 11, fill: '#94a3b8', textAnchor: 'middle', dominantBaseline: 'auto' }}>
                        {label}
                    </text>
                )}
            </svg>

            {(completed !== undefined && total !== undefined) && (
                <p className={styles.circularMeta}>
                    <span className={styles.circularMetaHighlight}>{completed}</span> / {total} complete
                </p>
            )}

            {trendPct !== null && trendPct !== undefined && (
                <span className={`${styles.circularTrend} ${trendPct >= 0 ? styles.trendUp : styles.trendDown}`}>
                    {trendPct >= 0 ? '↑' : '↓'} {Math.abs(trendPct).toFixed(1)}% from last month
                </span>
            )}
        </div>
    );
};

export default CircularProgressWidget;
